/**
 * AI 三螺旋管家。
 *
 * 产品定位（这一屏存在的唯一理由）：
 *   AI 不是问诊机器人，而是跨维度联想者 —— 它同时看到健康、企业、财富三份数据，
 *   并说出三者之间的关联；结论可解释（归因链）、可追溯（本次用到哪些数据域）、
 *   最终由人类专家确认。
 *
 * 与原型的关键差别：
 *   - 模型能力如实展示：接的是真大模型还是平台规则引擎，看 /api/ai/status 说什么就显示什么；
 *   - 归因链来自后端 citations（真实计算），不是写死的推理文本；
 *   - 会话落库（/api/ai/sessions），切屏与刷新都不丢，原型一切屏即丢；
 *   - 未授权的数据域会被后端拒用并回传 deniedScopes，页面如实提示，不假装"已同步全部数据"。
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { AiCitation, AiMessage, AiSession, AiStatus, ConsentScopeKey, Overview } from '@hew3/shared';
import { consentScopes } from '@hew3/shared';
import { api, streamChat } from '../../lib/api.ts';
import type { ChatMeta } from '../../lib/api.ts';
import { formatDateTime } from '../../lib/format.ts';
import { useUi } from '../../store/ui.ts';
import { Badge, Button, Card, Sheet, StateBox } from '../../components/ui.tsx';

/* ------------------------------------------------------------------ *
 * 本地视图模型：把「落库消息」与「正在流式生成的消息」统一成一种形状
 * ------------------------------------------------------------------ */

interface ViewAction {
  label: string;
  kind: string;
  payload?: Record<string, string>;
}

interface ViewMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations: AiCitation[];
  actions: ViewAction[];
  /** 本次回答真实调用了哪些数据域（后端 meta 回传，非前端推测） */
  dataScopes: ConsentScopeKey[];
  /** 因未授权而本次未使用的数据域 */
  deniedScopes: ConsentScopeKey[];
  deniedNotice: string;
  createdAt: string;
  streaming: boolean;
}

const consentScopeKeys: readonly string[] = consentScopes.map((s) => s.key);

function isConsentScope(key: string): key is ConsentScopeKey {
  return consentScopeKeys.includes(key);
}

function toScopeKeys(keys: string[]): ConsentScopeKey[] {
  return keys.filter(isConsentScope);
}

function scopeLabel(key: ConsentScopeKey): string {
  const found = consentScopes.find((s) => s.key === key);
  return found ? found.label : String(key);
}

function scopeText(keys: ConsentScopeKey[]): string {
  return keys.length === 0 ? '无' : keys.map(scopeLabel).join('、');
}

function toViewMessage(m: AiMessage): ViewMessage {
  return {
    id: m.id,
    role: m.role,
    content: m.content,
    citations: m.citations,
    actions: m.actions,
    dataScopes: m.dataScopes,
    deniedScopes: [],
    deniedNotice: '',
    createdAt: m.createdAt,
    streaming: false,
  };
}

/* ------------------------------------------------------------------ *
 * 快捷提问：覆盖规则引擎支持的全部意图，让成员知道「可以问什么」。
 * 跨维度联想才是本产品的差异点，因此首选跨域问题。
 * ------------------------------------------------------------------ */

const quickPrompts = [
  '我最近为什么容易疲劳？',
  '现金流下行会影响我的健康吗？',
  '接班人培养进度如何？',
  '帮我预约 301 专家',
  '我的财富健康安排是否稳妥？',
  '我最近的指标有哪些变化？',
];

interface MessagesResponse {
  items: AiMessage[];
}

export function AiPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showToast = useUi((s) => s.showToast);
  const lastAiSessionId = useUi((s) => s.lastAiSessionId);
  const setLastAiSession = useUi((s) => s.setLastAiSession);

  const [messages, setMessages] = useState<ViewMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  /**
   * onMeta 之后流式增量要落到「落库消息 id」上，用 ref 记录。
   * 不用 state：增量回调必须在同一帧拿到最新 id，state 更新是异步的。
   */
  const streamIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  /** 已同步到本地列表的历史消息载荷标记，避免每次渲染都覆盖流式内容 */
  const appliedRef = useRef<string>('');
  const chatRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  /* ---------------- 数据源 ---------------- */

  const statusQuery = useQuery({
    queryKey: ['ai', 'status'],
    queryFn: () => api.get<AiStatus>('/api/ai/status'),
  });

  // 问候语里的数字必须来自真实档案，因此这里复用首页总览接口
  const overviewQuery = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.get<Overview>('/api/overview'),
  });

  const sessionsQuery = useQuery({
    queryKey: ['ai', 'sessions'],
    queryFn: () => api.get<AiSession[]>('/api/ai/sessions'),
  });

  const sessionId = lastAiSessionId;
  const historyQuery = useQuery({
    queryKey: ['ai', 'messages', sessionId],
    queryFn: () => api.get<MessagesResponse>(`/api/ai/sessions/${sessionId}/messages`),
    enabled: sessionId !== null,
  });

  // 历史会话载入（仅在载荷变化时套用一次，之后由本地列表接管，含流式增量）
  useEffect(() => {
    const data = historyQuery.data;
    if (!data || sessionId === null) return;
    const marker = `${sessionId}:${data.items.length}`;
    if (appliedRef.current === marker) return;
    appliedRef.current = marker;
    setMessages(data.items.map(toViewMessage));
  }, [historyQuery.data, sessionId]);

  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streaming]);

  /* ---------------- 提问 ---------------- */

  const send = async (raw: string): Promise<void> => {
    const message = raw.trim();
    if (message.length === 0 || streaming) return;

    const userMessage: ViewMessage = {
      id: `local-user-${Date.now()}`,
      role: 'user',
      content: message,
      citations: [],
      actions: [],
      dataScopes: [],
      deniedScopes: [],
      deniedNotice: '',
      createdAt: new Date().toISOString(),
      streaming: false,
    };
    // 流式占位消息：id 在 onMeta 到达后替换为落库 id
    const placeholderId = `stream-${Date.now()}`;
    const placeholder: ViewMessage = {
      id: placeholderId,
      role: 'assistant',
      content: '',
      citations: [],
      actions: [],
      dataScopes: [],
      deniedScopes: [],
      deniedNotice: '',
      createdAt: new Date().toISOString(),
      streaming: true,
    };

    setInput('');
    setStreaming(true);
    setMessages((prev) => [...prev, userMessage, placeholder]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamChat(
        message,
        sessionId ?? undefined,
        {
          onMeta: (meta: ChatMeta) => {
            // 会话落库后记录 id，切屏与刷新后仍能回到这次对话
            setLastAiSession(meta.sessionId);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === placeholderId
                  ? {
                      ...m,
                      id: meta.messageId,
                      citations: meta.citations,
                      actions: meta.actions,
                      dataScopes: toScopeKeys(meta.dataScopes),
                      deniedScopes: toScopeKeys(meta.deniedScopes),
                      deniedNotice: meta.deniedNotice,
                    }
                  : m,
              ),
            );
            // 占位 id 已替换，后续增量按落库 id 追加
            streamIdRef.current = meta.messageId;
          },
          onDelta: (text: string) => {
            const targetId = streamIdRef.current ?? placeholderId;
            if (!mountedRef.current) return;
            setMessages((prev) =>
              prev.map((m) => (m.id === targetId ? { ...m, content: m.content + text, streaming: true } : m)),
            );
          },
          onDone: ({ messageId, content }) => {
            const targetId = streamIdRef.current ?? messageId;
            if (!mountedRef.current) return;
            setMessages((prev) =>
              prev.map((m) => (m.id === targetId ? { ...m, id: messageId, content, streaming: false } : m)),
            );
            void queryClient.invalidateQueries({ queryKey: ['ai', 'sessions'] });
          },
          onError: (msg: string) => {
            // 服务端已自动降级并回传可读文案，这里只做提示，不重复降级
            showToast(msg || 'AI 服务暂时不可用，请稍后重试', 'error');
          },
        },
        controller.signal,
      );
    } catch {
      // 主动「停止」属于预期中断，不再打扰用户
      if (!controller.signal.aborted) {
        showToast('AI 服务连接中断，请稍后重试', 'error');
      }
    } finally {
      streamIdRef.current = null;
      abortRef.current = null;
      if (mountedRef.current) setStreaming(false);
    }
  };

  const stop = (): void => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
    setMessages((prev) => prev.map((m) => (m.streaming ? { ...m, streaming: false, content: `${m.content}\n（已停止生成）` } : m)));
  };

  /* ---------------- 建议动作 ---------------- */

  const runAction = (action: ViewAction): void => {
    if (action.kind === 'book') {
      navigate('/service');
      return;
    }
    if (action.kind === 'navigate') {
      const route = action.payload?.route;
      if (route) navigate(route);
      else showToast('该建议动作缺少跳转目标', 'error');
      return;
    }
    if (action.kind === 'task') {
      showToast('已记录该建议动作，可在首页「今日守护」中查看');
      return;
    }
    showToast('该建议为说明性内容，无需跳转');
  };

  /* ---------------- 加载 / 失败 ---------------- */

  if (statusQuery.isLoading) {
    return (
      <div className="page">
        <div className="scr-head">
          <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
            <svg viewBox="0 0 24 24">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <div className="scr-title">
            AI 三螺旋管家<small>跨维度联想 · 可解释 · 专家确认</small>
          </div>
        </div>
        <div className="skeleton skel-card" />
        <div className="skeleton skel-card" style={{ height: 220 }} />
      </div>
    );
  }

  if (statusQuery.isError || !statusQuery.data) {
    return (
      <div className="page">
        <div className="scr-head">
          <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
            <svg viewBox="0 0 24 24">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <div className="scr-title">
            AI 三螺旋管家<small>跨维度联想 · 可解释 · 专家确认</small>
          </div>
        </div>
        <StateBox
          kind="error"
          title="AI 能力状态加载失败"
          desc="无法确认当前使用的是大模型还是平台规则引擎，为避免误述能力，暂不开放对话。"
          action={<Button small onClick={() => void statusQuery.refetch()}>重新加载</Button>}
        />
      </div>
    );
  }

  const status = statusQuery.data;
  const overview = overviewQuery.data;
  const activeSessions = sessionsQuery.data ?? [];
  const greetingSub = overview
    ? `已读取您的档案：H-Score ${overview.health.score} 分、企业健康度 ${overview.helix.e} 分、财富健康 ${overview.helix.w} 分。`
    : '正在读取您的档案（健康 / 企业 / 财富三份数据）…';

  return (
    <div className="page">
      {/* ---------------- 返回头 ---------------- */}
      <div className="scr-head">
        <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
          <svg viewBox="0 0 24 24">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <div className="scr-title">
          AI 三螺旋管家<small>跨维度联想 · 可解释 · 专家确认</small>
        </div>
        <button
          type="button"
          className="btn sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => setSessionsOpen(true)}
        >
          历史会话
        </button>
      </div>

      {/* ---------------- 能力状态条（如实展示） ---------------- */}
      <Card tight>
        <div className="row between" style={{ alignItems: 'flex-start' }}>
          <div className="grow">
            <div className="row-flex" style={{ gap: 8 }}>
              <Badge tone={status.mode === 'llm' ? 'e' : 'h'}>
                {status.mode === 'llm'
                  ? `已接入大模型 ${status.model ?? ''}`
                  : '平台内置规则引擎 · 既定配置'}
              </Badge>
              <span className="text-tiny text-muted">AI 能力状态 · /api/ai/status</span>
            </div>
            <div className="text-tiny text-muted" style={{ marginTop: 8, lineHeight: 1.7 }}>
              {status.notice}
            </div>
            <div className="text-tiny text-muted" style={{ marginTop: 8, lineHeight: 1.7 }}>
              AI 不是问诊机器人，而是跨维度联想者 —— 它存在的唯一理由是能同时看到健康、企业、财富三份数据，
              并说出三者之间的关联。
            </div>
          </div>
        </div>
        {status.mode === 'rules' && (
          <div className="note-src">
            <b>为什么用规则引擎</b>：这是本平台的既定配置，不是能力欠缺。健康与财富数据上，
            「回答确定、不会编造数字、每条结论可逐条追溯到档案来源」比「能闲聊」重要得多。
            规则引擎的每个回答都由您的档案数据直接推导，并附归因链与所用数据域；
            智能问答接入生成式模型属于后续增强项，届时接口会如实切换为 mode=llm，
            但归因链仍由平台计算，不会交给模型。
          </div>
        )}
      </Card>

      {/* ---------------- 对话流 ---------------- */}
      <div
        className="chat-wrap"
        ref={chatRef}
        style={{ maxHeight: 360, overflowY: 'auto' }}
      >
        {messages.length === 0 && (
          <div className="msg">
            <div className="av">AI</div>
            <div className="bubble">
              {greetingSub}
              <br />
              我是三螺旋管家。我可以把健康、企业、财富三条线索放在一起看 —— 例如现金流压力如何传导到睡眠与
              恢复能力，或接班人培养节奏与企业风险敞口的关系。请直接提问。
            </div>
          </div>
        )}

        {sessionId !== null && historyQuery.isLoading && (
          <div className="text-tiny text-muted">正在载入上次会话…</div>
        )}
        {sessionId !== null && historyQuery.isError && (
          <div className="note-src">
            上次会话历史加载失败。
            <button type="button" className="btn sm" style={{ marginLeft: 8 }} onClick={() => void historyQuery.refetch()}>
              重试
            </button>
          </div>
        )}

        {messages.map((m) =>
          m.role === 'user' ? (
            <div key={m.id} className="msg me">
              <div className="bubble">{m.content}</div>
            </div>
          ) : (
            <div key={m.id}>
              <div className="msg">
                <div className="av">AI</div>
                <div className="bubble" style={{ whiteSpace: 'pre-wrap' }}>
                  {m.content}
                  {m.streaming && <span className="stream-caret" />}
                </div>
              </div>

              {/* XAI 因果推理链：逐条渲染后端计算的归因项 */}
              {m.citations.length > 0 && (
                <div className="xai-card">
                  <div className="xai-hd" style={{ fontSize: 11 }}>
                    归因链由平台根据您的档案计算，非模型生成
                  </div>
                  {m.citations.map((c, i) => (
                    <div key={`${m.id}-cite-${i}`} className="xai-step">
                      <span className="xi">{i + 1}</span>
                      <span>{c.label}</span>
                      <span className="xc" style={c.direction === 'negative' ? { color: 'var(--danger)' } : undefined}>
                        {c.contribution}%
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* 本次回答用到了哪些数据域（可审计） */}
              {m.dataScopes.length > 0 && (
                <div className="sec-note">
                  本次回答调用了：{scopeText(m.dataScopes)}
                </div>
              )}

              {/* 未授权数据域如实提示 */}
              {(m.deniedScopes.length > 0 || m.deniedNotice.length > 0) && (
                <div className="note-src">
                  <b>部分数据域未获授权，本次回答未使用</b>
                  {m.deniedNotice.length > 0 && <>：{m.deniedNotice}</>}
                  <br />
                  未授权范围：{scopeText(m.deniedScopes)}。可在「我的 · 隐私与授权」中开启后重新提问。
                </div>
              )}

              {/* 建议动作 */}
              {m.actions.length > 0 && (
                <div className="chip-row">
                  {m.actions.map((a, i) => (
                    <button
                      key={`${m.id}-act-${i}`}
                      type="button"
                      className="chip"
                      style={{ fontSize: 11.5 }}
                      onClick={() => runAction(a)}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ),
        )}
      </div>

      {/* ---------------- 快捷提问 ---------------- */}
      <div className="chip-row">
        {quickPrompts.map((p) => (
          <button key={p} type="button" className="chip" disabled={streaming} onClick={() => void send(p)}>
            {p}
          </button>
        ))}
      </div>

      {/* ---------------- 输入区 ---------------- */}
      <div className="chat-input" style={{ position: 'sticky', bottom: 10, zIndex: 5 }}>
        <input
          value={input}
          maxLength={2000}
          disabled={streaming}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send(input);
            }
          }}
          placeholder={streaming ? '正在生成回答…' : '问一个跨维度的问题，例如「现金流下行会影响我的健康吗」'}
          aria-label="向 AI 三螺旋管家提问"
        />
        <button
          type="button"
          className="send"
          aria-label={streaming ? '停止生成' : '发送'}
          style={streaming ? { background: 'linear-gradient(120deg,#B2263F,#FF5D7E)' } : undefined}
          disabled={!streaming && input.trim().length === 0}
          onClick={() => {
            if (streaming) stop();
            else void send(input);
          }}
        >
          {streaming ? (
            <svg viewBox="0 0 24 24">
              <path d="M7 7h10v10H7z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24">
              <path d="M4 12h14M12 5l7 7-7 7" />
            </svg>
          )}
        </button>
      </div>

      <div className="row between" style={{ marginTop: 8 }}>
        <span className="sec-note" style={{ marginTop: 0 }}>
          {streaming ? '正在生成，可随时停止' : 'AI 仅做辅助研判，不构成诊断或投资建议，最终由各领域人类专家确认落地。'}
        </span>
        {streaming && (
          <button type="button" className="btn sm" onClick={stop}>
            停止
          </button>
        )}
      </div>

      {/* ---------------- 会话落库说明 ---------------- */}
      <div className="sec-note">
        本次对话已落库保存：切屏、切标签、刷新页面后都不会丢失（原型一切屏即丢）。
        会话记录同时受四级授权与审计留痕约束，撤回授权后相关数据不会被再次调用。
      </div>

      {/* ---------------- 历史会话列表 ---------------- */}
      <Sheet open={sessionsOpen} title="历史会话" onClose={() => setSessionsOpen(false)}>
        {sessionsQuery.isLoading && <StateBox kind="loading" title="正在加载会话…" />}
        {sessionsQuery.isError && (
          <StateBox
            kind="error"
            title="会话列表加载失败"
            desc="请稍后重试"
            action={<Button small onClick={() => void sessionsQuery.refetch()}>重新加载</Button>}
          />
        )}
        {sessionsQuery.data && activeSessions.length === 0 && (
          <StateBox title="还没有历史会话" desc="提出第一个问题后，会话会自动保存到这里" />
        )}
        {activeSessions.map((s) => (
          <button
            key={s.id}
            type="button"
            className="list-item"
            style={{ width: '100%', textAlign: 'left', border: 'none' }}
            onClick={() => {
              setLastAiSession(s.id);
              setSessionsOpen(false);
            }}
          >
            <div className="li-main">
              <div className="li-t">{s.title}</div>
              <div className="li-s">
                {s.messageCount} 条消息 · {formatDateTime(s.createdAt)}
              </div>
            </div>
            <div className="li-r">{s.id === sessionId ? <Badge tone="h">当前</Badge> : null}</div>
          </button>
        ))}
      </Sheet>
    </div>
  );
}
